import type {HTMLAttributes} from 'react';

import {cn} from '@/utils/ui';

type SkeletonProps = HTMLAttributes<HTMLDivElement>;

const Skeleton = ({className, ...props}: SkeletonProps) => {
  return (
    <div
      className={cn(
        'animate-pulse overflow-hidden rounded-soft bg-neutral-300/40',
        className
      )}
      {...props}
    />
  );
};

type FormSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
};

const FormSkeleton = ({className, size = 5, ...props}: FormSkeletonProps) => {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {Array.from({length: size}).map((_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <Skeleton className="h-4 max-w-40 rounded-full bg-neutral-200/70" />
          <Skeleton className="h-10 w-full rounded-sharp bg-neutral-200/60" />
        </div>
      ))}
    </div>
  );
};

type GridSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
  skeletonClassName?: string;
};

const GridSkeleton = ({
  className,
  skeletonClassName,
  size = 6,
  ...props
}: GridSkeletonProps) => {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3',
        className
      )}
      {...props}
    >
      {Array.from({length: size}).map((_, index) => (
        <div
          key={index}
          className={cn(
            'flex h-80 w-full flex-col rounded-soft border border-neutral-200/70 bg-white/80 p-5 shadow-subtle',
            skeletonClassName
          )}
        >
          <Skeleton className="h-5 w-3/4 rounded-full bg-neutral-300/50" />
          <Skeleton className="mt-2 h-4 w-1/2 rounded-full bg-neutral-200/80" />
          <Skeleton className="mt-6 h-32 w-full bg-neutral-200/70" />
          <div className="mt-4 flex gap-3">
            <Skeleton className="h-9 flex-1 rounded-full bg-neutral-200/80" />
            <Skeleton className="h-9 flex-1 rounded-full bg-neutral-200/80" />
          </div>
        </div>
      ))}
    </div>
  );
};

type ListSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  actionsCount?: number;
  size?: number;
  skeletonClassName?: string;
  withActions?: boolean;
};

const ListSkeleton = ({
  actionsCount = 2,
  className,
  skeletonClassName,
  size = 5,
  withActions = true,
  ...props
}: ListSkeletonProps) => {
  return (
    <div className={cn('flex flex-col gap-3', className)} {...props}>
      {Array.from({length: size}).map((_, index) => (
        <div
          key={index}
          className={cn(
            'flex items-center justify-between rounded-soft border border-neutral-200/70 bg-white/80 p-4 shadow-subtle',
            skeletonClassName
          )}
        >
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-1/2 rounded-full bg-neutral-300/50" />
            <Skeleton className="h-3 w-1/3 rounded-full bg-neutral-200/70" />
          </div>

          {withActions && (
            <div className="ml-4 flex items-center gap-2">
              {Array.from({length: actionsCount}).map((_, i) => (
                <Skeleton key={i} className="size-7 rounded-full" />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

type ChartSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  loadingLabel?: string;
};

const ChartSkeleton = ({
  className,
  loadingLabel,
  ...props
}: ChartSkeletonProps) => {
  return (
    <div className={cn('h-full min-h-60', className)} {...props}>
      <div className="relative h-full min-h-60 overflow-hidden rounded-soft border border-neutral-200/60 bg-neutral-100/30">
        <div className="absolute inset-0 flex flex-col justify-between p-4">
          <div className="border-t border-dashed border-neutral-200/80" />
          <div className="border-t border-dashed border-neutral-200/80" />
          <div className="border-t border-dashed border-neutral-200/80" />
          <div className="border-t border-dashed border-neutral-200/80" />
        </div>

        <div className="absolute inset-x-4 top-5 bottom-5">
          <div className="absolute top-[60%] left-[8%] h-1 w-[24%] -rotate-12 animate-pulse rounded-full bg-mint-300/70" />
          <div className="absolute top-[48%] left-[30%] h-1 w-[22%] rotate-[8deg] animate-pulse rounded-full bg-mint-300/70" />
          <div className="absolute top-[36%] left-[49%] h-1 w-[20%] rotate-[-10deg] animate-pulse rounded-full bg-mint-300/70" />
          <div className="absolute top-[44%] left-[66%] h-1 w-[18%] rotate-[14deg] animate-pulse rounded-full bg-mint-300/70" />
          <div className="absolute top-[58%] left-[22%] size-2 animate-pulse rounded-full bg-mint-300/80" />
          <div className="absolute top-[43%] left-[45%] size-2 animate-pulse rounded-full bg-mint-300/80" />
          <div className="absolute top-[38%] left-[64%] size-2 animate-pulse rounded-full bg-mint-300/80" />
        </div>
      </div>

      {loadingLabel && <span className="sr-only">{loadingLabel}</span>}
    </div>
  );
};

type TaskSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
};

const TaskSkeleton = ({className, size = 8, ...props}: TaskSkeletonProps) => {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {Array.from({length: size}).map((_, index) => (
        <div
          key={index}
          className={cn(
            'flex animate-pulse items-center justify-between rounded-full border border-neutral-300 bg-white px-6 py-3'
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <div className="size-6 rounded-full border border-neutral-300" />

            <div className="min-w-0 flex-1">
              <div className="h-4 w-2/3 rounded bg-neutral-300/50" />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="flex size-9 items-center justify-center rounded-full border-2 border-neutral-300">
                  <div className="size-6 rounded-full bg-neutral-300/50" />
                </div>

                <div className="h-7 w-28 rounded-full bg-neutral-200/60" />
              </div>
            </div>
          </div>

          <div className="ml-6 flex items-center gap-4 border-l border-neutral-200 pl-6">
            <div className="size-6 rounded-full bg-neutral-300/50" />
            <div className="size-6 rounded-full bg-neutral-300/50" />
            <div className="size-6 rounded-full bg-neutral-300/50" />
          </div>
        </div>
      ))}
    </div>
  );
};

type PageSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  actionsCount?: number;
  contentClassName?: string;
  fieldsPerSection?: number;
  parts?: ReadonlyArray<PageSkeletonPart>;
  sections?: number;
  size?: number;
  skeletonClassName?: string;
  toolbarActionsCount?: number;
  withActions?: boolean;
  view: 'form' | 'grid' | 'list' | 'task';
};

type PageSkeletonPart = 'action' | 'backButton' | 'stats' | 'title' | 'toolbar';

const PAGE_SKELETON_DEFAULT_PARTS: ReadonlyArray<PageSkeletonPart> = [];

type PageSkeletonHeaderProps = {
  withAction: boolean;
  withBackButton: boolean;
  withTitle: boolean;
};

const PageSkeletonHeader = ({
  withAction,
  withBackButton,
  withTitle,
}: PageSkeletonHeaderProps) => {
  const showHeader = withBackButton || withTitle || withAction;

  if (!showHeader) return null;

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {withBackButton && <Skeleton className="size-9 rounded-full" />}
      {withTitle && <Skeleton className="h-9 w-64 max-w-full rounded-full" />}
      {withAction && <Skeleton className="ml-auto h-10 w-40 rounded-full" />}
    </div>
  );
};

type PageSkeletonStatsProps = {
  withStats: boolean;
};

const PageSkeletonStats = ({withStats}: PageSkeletonStatsProps) => {
  if (!withStats) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({length: 2}).map((_, index) => (
        <Skeleton
          key={index}
          className="h-8 w-full border border-neutral-200/70 bg-white/80 shadow-subtle"
        />
      ))}
    </div>
  );
};

type PageSkeletonToolbarProps = {
  toolbarActionsCount: number;
  withToolbar: boolean;
};

const PageSkeletonToolbar = ({
  toolbarActionsCount,
  withToolbar,
}: PageSkeletonToolbarProps) => {
  if (!withToolbar) return null;

  return (
    <div className="flex flex-row flex-wrap items-center justify-end gap-2">
      <Skeleton className="h-10 min-w-64 grow rounded-full border border-neutral-200/70 bg-white/80" />
      {Array.from({length: toolbarActionsCount}).map((_, index) => (
        <Skeleton
          key={index}
          className="h-10 w-36 rounded-full bg-neutral-200/70"
        />
      ))}
    </div>
  );
};

type PageSkeletonFormViewProps = {
  fieldsPerSection: number;
  sections: number;
};

const PageSkeletonFormView = ({
  fieldsPerSection,
  sections,
}: PageSkeletonFormViewProps) => {
  return (
    <div className="rounded-soft bg-card p-5 shadow-subtle md:p-10">
      <div className="flex max-w-4xl flex-col gap-12">
        {Array.from({length: sections}).map((_, sectionIndex) => (
          <div key={sectionIndex} className="flex gap-5 max-md:flex-col">
            <div className="md:w-1/4">
              <Skeleton className="h-6 w-36 rounded-full" />
            </div>
            <FormSkeleton className="md:w-3/4" size={fieldsPerSection} />
          </div>
        ))}
      </div>
    </div>
  );
};

type PageSkeletonContentProps = Pick<
  PageSkeletonProps,
  | 'actionsCount'
  | 'contentClassName'
  | 'fieldsPerSection'
  | 'sections'
  | 'size'
  | 'skeletonClassName'
  | 'view'
  | 'withActions'
>;

const PageSkeletonContent = ({
  actionsCount,
  contentClassName,
  fieldsPerSection = 3,
  sections = 2,
  size,
  skeletonClassName,
  view,
  withActions,
}: PageSkeletonContentProps) => {
  if (view === 'form') {
    return (
      <PageSkeletonFormView
        fieldsPerSection={fieldsPerSection}
        sections={sections}
      />
    );
  }

  if (view === 'list') {
    return (
      <ListSkeleton
        actionsCount={actionsCount}
        className={contentClassName}
        size={size ?? 6}
        skeletonClassName={skeletonClassName}
        withActions={withActions}
      />
    );
  }

  if (view === 'task') {
    return <TaskSkeleton className={contentClassName} size={size} />;
  }

  return (
    <GridSkeleton
      className={contentClassName}
      size={size ?? 6}
      skeletonClassName={skeletonClassName}
    />
  );
};

const PageSkeleton = ({
  actionsCount,
  className,
  contentClassName,
  fieldsPerSection = 3,
  parts = PAGE_SKELETON_DEFAULT_PARTS,
  sections = 2,
  size,
  skeletonClassName,
  toolbarActionsCount = 1,
  view,
  withActions,
  ...props
}: PageSkeletonProps) => {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      <PageSkeletonHeader
        withAction={parts.includes('action')}
        withBackButton={parts.includes('backButton')}
        withTitle={parts.includes('title')}
      />
      <PageSkeletonStats withStats={parts.includes('stats')} />
      <PageSkeletonToolbar
        toolbarActionsCount={toolbarActionsCount}
        withToolbar={parts.includes('toolbar')}
      />
      <PageSkeletonContent
        actionsCount={actionsCount}
        contentClassName={contentClassName}
        fieldsPerSection={fieldsPerSection}
        sections={sections}
        size={size}
        skeletonClassName={skeletonClassName}
        view={view}
        withActions={withActions}
      />
    </div>
  );
};

export {
  ChartSkeleton,
  FormSkeleton,
  GridSkeleton,
  ListSkeleton,
  PageSkeleton,
  Skeleton,
  TaskSkeleton,
};
